import React, { ReactElement, useState } from 'react'
import { useFormik } from 'formik'
import * as yup from 'yup'
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  FormControl,
  FormControlLabel,
  FormLabel,
  Paper,
  Radio,
  RadioGroup,
  Snackbar,
  TextField,
  Typography,
} from '@mui/material'
import { ConfirmIcon, FeedbackIcon } from 'src/v3/core/icons'
import Loading from 'src/v3/components/loading'

let validText = /.*\S.*/

export type SurveyFormValues = {
  topicsThoroughAndInteresting: number | null
  presenterGoodUnderstanding: number | null
  videoQualityProfessional: number | null
  benefitCareerGoals: number | null
  knowledgePositiveImpactPractice: number | null
  activityPositiveImpactPractice: number | null
  confusingTopics: string | null
  thingsToBeContinued: string | null
  thingsToBeImproved: string | null
  explainImpactPractice: string | null
}

export default ({
  onSubmit,
  initialValues,
  domain,
}: {
  onSubmit: (values: SurveyFormValues) => Promise<void>
  initialValues?: Partial<SurveyFormValues>
  domain: string
}): ReactElement => {
  let [loading, setLoading] = useState<boolean>(false)
  let [errorMessage, setErrorMessage] = useState<string>('')

  let form = useFormik({
    validationSchema: yup.object({
      topicsThoroughAndInteresting: yup
        .number()
        .required('This field is required'),
      presenterGoodUnderstanding: yup.number().required(),
      videoQualityProfessional: yup.number().required(),
      benefitCareerGoals: yup.number().required(),
      knowledgePositiveImpactPractice: yup.number().required(),
      activityPositiveImpactPractice: yup.number().required(),
      confusingTopics: yup
        .string()
        .matches(validText)
        .required('This field is required')
        .nullable(),
      thingsToBeContinued: yup
        .string()
        .matches(validText)
        .required('This field is required')
        .nullable(),
      thingsToBeImproved: yup
        .string()
        .matches(validText)
        .required('This field is required')
        .nullable(),
      explainImpactPractice: yup
        .string()
        .matches(validText)
        .required('This field is required')
        .nullable(),
    }),
    initialValues: {
      topicsThoroughAndInteresting:
        initialValues?.topicsThoroughAndInteresting || null,
      presenterGoodUnderstanding:
        initialValues?.presenterGoodUnderstanding || null,
      videoQualityProfessional: initialValues?.videoQualityProfessional || null,
      benefitCareerGoals: initialValues?.benefitCareerGoals || null,
      knowledgePositiveImpactPractice:
        initialValues?.knowledgePositiveImpactPractice || null,
      activityPositiveImpactPractice:
        initialValues?.activityPositiveImpactPractice || null,
      confusingTopics: initialValues?.confusingTopics || null,
      thingsToBeContinued: initialValues?.thingsToBeContinued || null,
      thingsToBeImproved: initialValues?.thingsToBeImproved || null,
      explainImpactPractice: initialValues?.explainImpactPractice || null,
    },
    enableReinitialize: true,
    onSubmit: async (values) => {
      setLoading(true)
      try {
        await onSubmit(values)
      } catch (err: any) {
        setErrorMessage(err?.response?.data || 'Survey submission failed')
      } finally {
        setLoading(false)
      }
    },
  })

  let RatingQuestion = ({
    name,
    label,
    error,
  }: {
    name: keyof SurveyFormValues
    label: string
    error: boolean
  }) => (
    <Paper
      elevation={0}
      sx={{
        p: 3,
        bgcolor: 'background.paperDark',
        borderRadius: 2,
        border: error ? '1px solid' : '1px solid transparent',
        borderColor: error ? 'error.main' : 'transparent',
      }}
    >
      <FormControl error={error} component="fieldset" sx={{ width: '100%' }}>
        <FormLabel
          component="legend"
          sx={{
            mb: 2,
            color: error ? 'error.main' : 'text.primary',
          }}
        >
          {label}
        </FormLabel>
        <RadioGroup
          aria-label={name}
          name={name}
          value={form.values[name] || ''}
          onChange={form.handleChange}
          sx={{
            flexDirection: 'row',
            gap: 2,
            '& .MuiFormControlLabel-root': {
              bgcolor: 'background.paper',
              borderRadius: 1,
              px: 2,
              py: 1,
              m: 0,
              transition: 'all 0.2s ease-in-out',
              '&:hover': {
                bgcolor: 'primary.dark',
              },
            },
            '& .Mui-checked + .MuiFormControlLabel-label': {
              color: 'primary.main',
              fontWeight: 600,
            },
          }}
        >
          {[1, 2, 3, 4, 5].map((value) => (
            <FormControlLabel
              key={value}
              value={value.toString()}
              control={<Radio size="small" />}
              label={value.toString()}
              sx={{
                '& .MuiFormControlLabel-label': {
                  fontSize: '0.875rem',
                  fontWeight: 500,
                },
              }}
            />
          ))}
        </RadioGroup>
      </FormControl>
    </Paper>
  )

  return (
    <>
      <form onSubmit={form.handleSubmit}>
        <Box sx={{ mb: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, m: 2 }}>
            <Avatar
              sx={{
                bgcolor: 'primary.main',
                width: 40,
                height: 40,
              }}
            >
              <FeedbackIcon />
            </Avatar>
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 700 }}>
                {domain} Evaluation Survey
              </Typography>
              <Typography variant="subtitle1" color="textSecondary">
                Your feedback helps us improve our educational content
              </Typography>
            </Box>
          </Box>
        </Box>

        <Card
          elevation={0}
          sx={{
            p: 4,
            bgcolor: 'background.paperDark',
            borderRadius: 3,
            mb: 4,
            border: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Typography
            variant="body1"
            sx={{
              mb: 4,
              color: 'text.secondary',
              fontSize: '1.1rem',
              textAlign: 'center',
              p: 2,
              bgcolor: 'background.paper',
              borderRadius: 2,
            }}
          >
            Please rate your responses on a scale of 1 to 5, with 1 being the
            lowest and 5 being the highest.
          </Typography>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <RatingQuestion
              name="topicsThoroughAndInteresting"
              label="Were the topics presented in a thorough and interesting manner?"
              error={
                !!form.touched.topicsThoroughAndInteresting &&
                !!form.errors.topicsThoroughAndInteresting
              }
            />

            <RatingQuestion
              name="presenterGoodUnderstanding"
              label="The presenter had a good understanding of the content being delivered?"
              error={
                !!form.touched.presenterGoodUnderstanding &&
                !!form.errors.presenterGoodUnderstanding
              }
            />

            <RatingQuestion
              name="videoQualityProfessional"
              label="The video quality was professional?"
              error={
                !!form.touched.videoQualityProfessional &&
                !!form.errors.videoQualityProfessional
              }
            />

            <RatingQuestion
              name="benefitCareerGoals"
              label="The materials presented will benefit me in my career goals?"
              error={
                !!form.touched.benefitCareerGoals &&
                !!form.errors.benefitCareerGoals
              }
            />

            <RatingQuestion
              name="knowledgePositiveImpactPractice"
              label="How strongly do you believe the knowledge gained from this educational activity will positively impact your practice?"
              error={
                !!form.touched.knowledgePositiveImpactPractice &&
                !!form.errors.knowledgePositiveImpactPractice
              }
            />

            <RatingQuestion
              name="activityPositiveImpactPractice"
              label="How strongly do you believe this educational activity will positively impact your practice?"
              error={
                !!form.touched.activityPositiveImpactPractice &&
                !!form.errors.activityPositiveImpactPractice
              }
            />
          </Box>
        </Card>

        <Card
          elevation={0}
          sx={{
            p: 4,
            bgcolor: 'background.paperDark',
            borderRadius: 3,
            mb: 4,
            border: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Typography
            variant="h6"
            sx={{
              mb: 3,
              fontWeight: 600,
              display: 'flex',
              alignItems: 'center',
              gap: 1,
            }}
          >
            <ConfirmIcon color="primary" />
            Additional Feedback
          </Typography>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <TextField
              name="confusingTopics"
              value={form.values.confusingTopics || ''}
              onChange={form.handleChange}
              error={
                form.touched.confusingTopics && !!form.errors.confusingTopics
              }
              helperText={
                form.touched.confusingTopics && form.errors.confusingTopics
              }
              label="Are there any topics that still confuse you?"
              multiline
              minRows={3}
              variant="outlined"
              fullWidth
              sx={{
                '& .MuiOutlinedInput-root': {
                  bgcolor: 'background.paper',
                  borderRadius: 2,
                  '& fieldset': {
                    borderColor: 'divider',
                  },
                  '&:hover fieldset': {
                    borderColor: 'primary.main',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: 'primary.main',
                  },
                },
              }}
            />

            <TextField
              name="thingsToBeContinued"
              value={form.values.thingsToBeContinued || ''}
              onChange={form.handleChange}
              error={
                form.touched.thingsToBeContinued &&
                !!form.errors.thingsToBeContinued
              }
              helperText={
                form.touched.thingsToBeContinued &&
                form.errors.thingsToBeContinued
              }
              label="Likes/strengths/things to be continued?"
              multiline
              minRows={3}
              variant="outlined"
              fullWidth
              sx={{
                '& .MuiOutlinedInput-root': {
                  bgcolor: 'background.paper',
                  borderRadius: 2,
                  '& fieldset': {
                    borderColor: 'divider',
                  },
                  '&:hover fieldset': {
                    borderColor: 'primary.main',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: 'primary.main',
                  },
                },
              }}
            />

            <TextField
              name="thingsToBeImproved"
              value={form.values.thingsToBeImproved || ''}
              onChange={form.handleChange}
              error={
                form.touched.thingsToBeImproved &&
                !!form.errors.thingsToBeImproved
              }
              helperText={
                form.touched.thingsToBeImproved &&
                form.errors.thingsToBeImproved
              }
              label="Dislikes/weaknesses/things to be changed or improved?"
              multiline
              minRows={3}
              variant="outlined"
              fullWidth
              sx={{
                '& .MuiOutlinedInput-root': {
                  bgcolor: 'background.paper',
                  borderRadius: 2,
                  '& fieldset': {
                    borderColor: 'divider',
                  },
                  '&:hover fieldset': {
                    borderColor: 'primary.main',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: 'primary.main',
                  },
                },
              }}
            />

            <TextField
              name="explainImpactPractice"
              value={form.values.explainImpactPractice || ''}
              onChange={form.handleChange}
              error={
                form.touched.explainImpactPractice &&
                !!form.errors.explainImpactPractice
              }
              helperText={
                form.touched.explainImpactPractice &&
                form.errors.explainImpactPractice
              }
              label="Discuss three things you learned during this educational activity and explain how at least one of them will impact your future practice."
              multiline
              minRows={4}
              variant="outlined"
              fullWidth
              sx={{
                '& .MuiOutlinedInput-root': {
                  bgcolor: 'background.paper',
                  borderRadius: 2,
                  '& fieldset': {
                    borderColor: 'divider',
                  },
                  '&:hover fieldset': {
                    borderColor: 'primary.main',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: 'primary.main',
                  },
                },
              }}
            />
          </Box>
        </Card>

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 4 }}>
          <Button
            type="submit"
            variant="contained"
            size="large"
            disabled={loading}
          >
            Submit Survey
          </Button>
        </Box>

        <Loading entities={[]} loading={loading} />

        <Snackbar open={!!errorMessage} onClose={() => setErrorMessage('')}>
          <Alert
            variant="filled"
            onClose={() => setErrorMessage('')}
            severity="error"
          >
            {errorMessage}
          </Alert>
        </Snackbar>
      </form>
    </>
  )
}
